import React, { useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop, mapValues } from 'lodash-es';
import { ButtonGroup, Row, Col } from 'reactstrap';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { MultiOfferModel, searchByVinPath } from 'client/data/models/multi-offer';

// Utils
import { getQuery } from 'client/utils/location';
import { getActiveTabId } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-active-tab-id';
import { initializeTabConfiguration } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/tab-initialization';
import { buildTabsList } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/build-tabs-list';

// Constants
import { APPRAISE_BY_TAB_HEADERS_CLASSNAME } from 'site-modules/shared/components/appraisal/appraisal-tabs/constants';
import { PARTNER_OFFER_ADDRESS_LOOKUP } from 'client/site-modules/shared/constants/appraisal/appraisal';
import {
  MMY_TAB_ID,
  VIN_TAB_ID,
  LICENSE_PLATE_TAB_ID,
  ADDRESS_TAB_ID,
  MMY_TAB,
  VIN_TAB,
  LICENSE_PLATE_TAB,
  ADDRESS_TAB,
  TABS,
  TAB_LINKS_ORDER_BY,
  DEFAULT_INITIAL_ACTIVE_TAB_ID,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tab-constants';

// Context
import {
  useAppraisalTabsConfig,
  useAppraisalTabsState,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';

// Hooks
import { useTabManagement } from 'client/site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-tab-management';
import { useAddressToVin } from 'client/site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-address-to-vin';

// Components
import { AppraiseByTabsHeaderItem } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraise-by-tabs-header-item';
import { MmyTab } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/mmy-tab/mmy-tab';
import { VinTab } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/vin-tab/vin-tab';
import { AddressTab } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/address-tab/address-tab';
import { LicensePlateTab } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/license-plate-tab/license-plate-tab';
import { TabsCopy } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/tabs-copy';
import { TabsContent } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/tab-content';
import { AppraisalUtmStorage } from 'site-modules/shared/components/appraisal/appraisal-utm-storage/appraisal-utm-storage';
import { AppraisalFindVinDrawer } from 'site-modules/shared/components/find-vin-modal/appraisal-find-vin-drawer';

import 'site-modules/shared/components/appraisal/appraisal-tabs-v2/appraisal-tabs.scss';

const MMY_TAB_WITH_COMPONENT = {
  ...MMY_TAB,
  TabComponent: MmyTab,
};
const VIN_TAB_WITH_COMPONENT = {
  ...VIN_TAB,
  TabComponent: VinTab,
};
const LICENSE_PLATE_TAB_WITH_COMPONENT = {
  ...LICENSE_PLATE_TAB,
  TabComponent: LicensePlateTab,
};
const ADDRESS_TAB_WITH_COMPONENT = {
  ...ADDRESS_TAB,
  TabComponent: AddressTab,
};
const TABS_WITH_COMPONENT_BY_ID = {
  [MMY_TAB_ID]: MMY_TAB_WITH_COMPONENT,
  [VIN_TAB_ID]: VIN_TAB_WITH_COMPONENT,
  [LICENSE_PLATE_TAB_ID]: LICENSE_PLATE_TAB_WITH_COMPONENT,
};

const TABS_WITH_COMPONENT = mapValues(TABS, tabs => tabs.map(({ id }) => TABS_WITH_COMPONENT_BY_ID[id]));

const TAB_LINKS_ORDER_BY_WITH_COMPONENT = mapValues(TAB_LINKS_ORDER_BY, ({ FIRST, SECOND }) => ({
  FIRST: TABS_WITH_COMPONENT_BY_ID[FIRST.id],
  SECOND: TABS_WITH_COMPONENT_BY_ID[SECOND.id],
}));

export const getAddressToVinDrawerClassName = displayAsLink => (displayAsLink ? 'mb-1 size-16' : 'mt-0_25 size-14');

function EdmundsLogo() {
  return (
    <div>
      <div className="d-inline-flex p-0_25 align-items-center text-primary mb-0_25 bg-transparent size-16 fw-normal">
        <i className="icon-edmunds-car size-30 me-0_25" aria-hidden />
        <span className="ps-0_5">Did you know?</span>
      </div>
    </div>
  );
}

export function AppraisalTabsLayoutUI({
  bgClassName = 'bg-white',
  className = '',
  title = '',
  subtitle = '',
  wrapperClassName = '',
  hideEdmundsLogo = false,
  ctaText = "See your car's value",
  ctaClassName = undefined,
  isYearDropdownFirst = true,
  initialStateCode = '',
  initialVehicle = null,
  setStylesAndVin = noop,
  isProfileOfferVinPrepopulationEnabled = false,
  profileOfferVin = '',
  hasAddressToVinDrawer = false,
  storage: storageFromProps = null,
  setModelValue = noop,
  isLicensePlateTabDefault = false,
  initialActiveTabId = DEFAULT_INITIAL_ACTIVE_TAB_ID,
  vinModsData = null,
}) {
  const {
    isLookUpStyleView,
    isBackgroundStyleTabEnabled,
    isEmoSearchEnabled,
    isSameHeight,
    withContainerPadding,
    renderAddressTab,
    isMobile,
    isSellCar,
    isSellCarLanding,
    creativeId,
    location,
    referer,
    isVisibleInDrawer,
    accentColorClassName,
    accentBgColorClassName,
    isProjectEveStyle,
  } = useAppraisalTabsConfig();

  // Mount-only: UTM storage checks (runs once on initial render)
  useEffect(() => {
    AppraisalUtmStorage.checkAndSetSemCampaignAppraisals(location);
    AppraisalUtmStorage.checkAndSetSemCampaignFacebook(location);
  }, []);

  // Build initial tabs configuration
  const { initialTabs, computedActiveTabId } = useMemo(() => {
    const { make, model, year, vin, tab } = getQuery(location);
    const hasMMYQuery = make && model && year;
    const hasVinQuery = !!vin;

    const { isLicensePlateTabDefaulted } = initializeTabConfiguration({
      tab,
      isLicensePlateTabDefault,
      isSellCar,
      referer,
    });

    const tabs = buildTabsList({
      isLicensePlateTabDefaulted,
      isLookUpStyleView,
      isLicensePlateTabDefault,
      renderAddressTab,
      tabsWithComponent: TABS_WITH_COMPONENT,
      addressTabWithComponent: ADDRESS_TAB_WITH_COMPONENT,
    });

    const activeTab = getActiveTabId({
      isLicensePlateTabDefault: isLicensePlateTabDefaulted,
      hasMMYQuery,
      hasVinQuery,
      isLookUpStyleView,
      initialActiveTabId,
    });

    return { initialTabs: tabs, computedActiveTabId: activeTab };
  }, []);

  const {
    activeTabId: contextActiveTabId,
    tabs: contextTabs,
    prepopulatedVehicle,
    setActiveTabId,
    setTabs,
    setPrepopulatedVehicle,
  } = useAppraisalTabsState();

  const { onAddressVin } = useAddressToVin();

  const {
    isPrivatePartyBtn,
    storage,
    setActiveTab,
    handleTabLinkClick,
    handlePrepopulatedVehicle,
    handleVinLookupBtnClick,
  } = useTabManagement({
    initialTabs,
    initialActiveTabId: computedActiveTabId,
    activeTabId: contextActiveTabId,
    tabs: contextTabs,
    storageFromProps,
    creativeId,
    isLookUpStyleView,
    isVisibleInDrawer,
    isProfileOfferVinPrepopulationEnabled,
    profileOfferVin,
    vinModsData,
    setActiveTabId,
    setTabs,
    setPrepopulatedVehicle,
  });

  // Falls back to the computed values for the current render pass — the context setters called
  // above during a descendant's render aren't guaranteed to commit synchronously during SSR.
  const activeTabId = contextActiveTabId || computedActiveTabId;
  const tabs = contextTabs.length ? contextTabs : initialTabs;

  const isAddressTabActive = activeTabId === ADDRESS_TAB_ID;

  const renderAddressToVinDrawer = useCallback(
    displayAsLink => {
      if (renderAddressTab) return null;

      const query = getQuery(location);

      return (
        <AppraisalFindVinDrawer
          activeTabId={activeTabId}
          isMobile={isMobile}
          creativeId={PARTNER_OFFER_ADDRESS_LOOKUP}
          updateVinAndContinue={onAddressVin}
          isLandingPage
          isBackgroundStyleTabEnabled={isBackgroundStyleTabEnabled || isProjectEveStyle}
          className={getAddressToVinDrawerClassName(displayAsLink)}
          displayAsLink={displayAsLink}
          vehicle={{
            make: { slug: prepopulatedVehicle?.makeSlug },
            model: { slug: prepopulatedVehicle?.modelSlug },
            year: { year: prepopulatedVehicle?.year },
          }}
          openCtaAsButtonClassName={classnames(
            {
              'ms-lg-0_5 mt-lg-0': isEmoSearchEnabled,
            },
            accentBgColorClassName
          )}
          iconClassName={accentColorClassName}
          licensePlateAndVinForm={
            <LicensePlateTab
              storage={storage}
              isMobile={isMobile}
              creativeId={creativeId}
              ctaText="Continue"
              query={query}
              setStylesAndVin={setStylesAndVin}
              isFullWidth
              isVinByAddressOverlay
            />
          }
        />
      );
    },
    [
      renderAddressTab,
      location,
      activeTabId,
      isMobile,
      onAddressVin,
      isBackgroundStyleTabEnabled,
      isProjectEveStyle,
      prepopulatedVehicle,
      isEmoSearchEnabled,
      accentBgColorClassName,
      accentColorClassName,
      storage,
      creativeId,
      setStylesAndVin,
    ]
  );

  const tabsCopyProps = {
    tabLinks: TAB_LINKS_ORDER_BY_WITH_COMPONENT[activeTabId],
    onTabLinkClick: handleTabLinkClick,
    onVinLookupBtnClick: handleVinLookupBtnClick,
    addressToVinDrawer: hasAddressToVinDrawer ? renderAddressToVinDrawer(true) : null,
    hasAddressToVinDrawer,
  };

  const tabsContentProps = {
    activeTabId,
    tabs,
    storage,
    onTabLinkClick: handleTabLinkClick,
    onPrepopulatedVehicle: handlePrepopulatedVehicle,
    onAddressVin,
    ctaText,
    ctaClassName,
    isYearDropdownFirst,
    initialStateCode,
    initialVehicle,
    setStylesAndVin,
    isProfileOfferVinPrepopulationEnabled,
    profileOfferVin,
    hasAddressToVinDrawer,
    setModelValue,
    isPrivatePartyBtn,
  };
  const hasContainerRoundedCorners = !isLookUpStyleView && withContainerPadding;

  return (
    <div
      className={classnames('appraisal-tabs-container-wrapper appraisal-tabs-v2', wrapperClassName, {
        'appraisal-tabs-container-wrapper-background-style-enabled': isBackgroundStyleTabEnabled,
        'is-same-height': isSameHeight,
        'has-address-tab': renderAddressTab,
      })}
      data-tracking-parent={creativeId}
    >
      {isBackgroundStyleTabEnabled && (
        <ButtonGroup size="md" role="tablist" className="tab-list">
          {tabs.map(({ id, label }, index) => (
            <AppraiseByTabsHeaderItem
              key={id}
              label={label}
              tabId={id}
              isActive={id === activeTabId}
              onBtnClick={setActiveTab}
              isFirstItem={index === 0}
              isLastItem={index === tabs.length - 1}
            />
          ))}
        </ButtonGroup>
      )}
      <div
        id="appraisal-tabs-container"
        className={classnames('appraisal-tabs-container', bgClassName, className, {
          rounded: hasContainerRoundedCorners && !isBackgroundStyleTabEnabled,
          'appraisal-tabs-container-background-style-rounded':
            hasContainerRoundedCorners && isBackgroundStyleTabEnabled,
          'is-emo-search-sell-car-landing': isEmoSearchEnabled && isSellCarLanding,
          'pb-lg-0_5': isSellCarLanding,
        })}
        role={isBackgroundStyleTabEnabled ? 'tabpanel' : undefined}
      >
        <div
          className={classnames({
            'p-1 p-md-1_5': !isLookUpStyleView && withContainerPadding,
          })}
        >
          {title && !isAddressTabActive && (
            <div className={classnames('heading-4', { 'mb-1_5': !isEmoSearchEnabled, 'mb-0_5': isEmoSearchEnabled })}>
              {title}
            </div>
          )}
          {subtitle && !isAddressTabActive && <div className="mb-1">{subtitle}</div>}
          <div
            className={classnames(`d-flex ${APPRAISE_BY_TAB_HEADERS_CLASSNAME}`, {
              'mb-1 mb-lg-1_5': isLookUpStyleView,
            })}
          >
            {isLookUpStyleView && (
              <span className="medium text-gray-darker fw-bold d-inline-block pb-0_5 me-0_5">Enter by:</span>
            )}
            {!isBackgroundStyleTabEnabled && (
              <ButtonGroup
                size="md"
                role="tablist"
                className={classnames('tabs-button-list', {
                  'with-address-tab': renderAddressTab,
                  'mb-1': isAddressTabActive,
                })}
              >
                {tabs.map(({ id, label }, index) => (
                  <AppraiseByTabsHeaderItem
                    key={id}
                    label={label}
                    tabId={id}
                    isActive={id === activeTabId}
                    onBtnClick={setActiveTab}
                    isFirstItem={index === 0}
                    isLastItem={index === tabs.length - 1}
                  />
                ))}
              </ButtonGroup>
            )}
          </div>
          {!isEmoSearchEnabled && isBackgroundStyleTabEnabled && !isAddressTabActive && (
            <div className="appraisal-tab-link-copy text-gray-dark size-14 mb-1_5">
              <TabsCopy {...tabsCopyProps} />
            </div>
          )}
          {!isLookUpStyleView && !isBackgroundStyleTabEnabled && !isAddressTabActive ? (
            <Row>
              <Col xs={12} className="appraisal-tab-link-copy text-gray-dark size-14 mt-1 mb-1_5">
                {!hideEdmundsLogo && <EdmundsLogo />}
                <TabsCopy {...tabsCopyProps} />
              </Col>
            </Row>
          ) : null}
          <TabsContent {...tabsContentProps} />
          {isEmoSearchEnabled && isBackgroundStyleTabEnabled && !isAddressTabActive && (
            <Row>
              <Col xs={12} className="appraisal-tab-link-copy text-gray-darker size-14 mt-1">
                <div
                  className={classnames(
                    'size-14 mb-1_5 d-lg-flex',
                    isSellCarLanding ? 'align-items-end' : 'align-items-center'
                  )}
                >
                  <TabsCopy {...tabsCopyProps} />
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </div>
  );
}

AppraisalTabsLayoutUI.propTypes = {
  bgClassName: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  wrapperClassName: PropTypes.string,
  hideEdmundsLogo: PropTypes.bool,
  ctaText: PropTypes.string,
  ctaClassName: PropTypes.string,
  isYearDropdownFirst: PropTypes.bool,
  initialStateCode: PropTypes.string,
  initialVehicle: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  setStylesAndVin: PropTypes.func,
  isProfileOfferVinPrepopulationEnabled: PropTypes.bool,
  profileOfferVin: PropTypes.string,
  hasAddressToVinDrawer: PropTypes.bool,
  storage: PropTypes.shape({}),
  setModelValue: PropTypes.func,
  isLicensePlateTabDefault: PropTypes.bool,
  initialActiveTabId: PropTypes.string,
  vinModsData: PropTypes.shape({}),
};

export const stateToPropsConfig = {
  vinModsData: bindToPath(({ vin }) => (vin ? searchByVinPath.build(vin) : null), MultiOfferModel),
};

export const AppraisalTabsLayout = connectToModel(AppraisalTabsLayoutUI, stateToPropsConfig);
